// frontend/src/components/MovieCard.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, Plus, Eye, Calendar } from 'lucide-react';

const TMDB_IMAGE_BASE = 'https://image.tmdb.org/t/p/w500';

export default function MovieCard({ movie, onAddToList }) {
  const navigate = useNavigate();
  const mediaType = movie.media_type || (movie.first_air_date ? 'tv' : 'movie');
  const title = movie.title || movie.name;
  const year = (movie.release_date || movie.first_air_date || '').substring(0, 4);
  const posterUrl = movie.poster_path
    ? `${TMDB_IMAGE_BASE}${movie.poster_path}`
    : null;

  const handleClick = (e) => {
    if (e.target.closest('[data-action]')) return;
    navigate(`/${mediaType}/${movie.id}`);
  };

  return (
    <div
      onClick={handleClick}
      className="group relative cursor-pointer rounded-xl overflow-hidden border border-white/5 bg-card hover:border-primary/50 transition-all duration-300 hover:scale-[1.03] hover:shadow-xl hover:shadow-black/50"
      data-testid={`movie-card-${movie.id}`}
    >
      {/* Poster */}
      <div className="aspect-[2/3] relative bg-muted overflow-hidden">
        {posterUrl ? (
          <img
            src={posterUrl}
            alt={title}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-muted to-card">
            <Eye className="w-10 h-10 text-muted-foreground opacity-30" />
          </div>
        )}

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

        {/* Rating badge — always visible */}
        {movie.vote_average > 0 && (
          <div className="absolute top-2 left-2 flex items-center gap-1 bg-black/60 backdrop-blur-md text-yellow-400 text-xs font-bold px-2 py-1 rounded-lg">
            <Star className="w-3 h-3 fill-current" />
            {movie.vote_average.toFixed(1)}
          </div>
        )}

        {/* Media type badge */}
        {mediaType === 'tv' && (
          <div className="absolute top-2 right-2 bg-secondary/80 backdrop-blur-md text-white text-[10px] font-bold px-2 py-0.5 rounded-lg">
            TV
          </div>
        )}

        {/* Quick-action bar — appears on hover */}
        <div className="absolute bottom-0 left-0 right-0 p-3 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
          <div className="flex gap-2">
            <button
              data-action="view"
              onClick={() => navigate(`/${mediaType}/${movie.id}`)}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-white/10 backdrop-blur-md border border-white/20 text-white text-xs font-semibold hover:bg-white/20 transition-colors"
            >
              <Eye className="w-3.5 h-3.5" /> View
            </button>
            {onAddToList && (
              <button
                data-action="add"
                onClick={(e) => { e.stopPropagation(); onAddToList(movie); }}
                className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-primary/80 backdrop-blur-md text-primary-foreground text-xs font-semibold hover:bg-primary transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="p-3 space-y-1">
        <h3 className="font-semibold text-sm text-foreground line-clamp-1 group-hover:text-primary transition-colors">
          {title}
        </h3>
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <Calendar className="w-3 h-3" /> {year || 'TBA'}
          </span>
          {movie.vote_count > 0 && (
            <span>{movie.vote_count?.toLocaleString()} votes</span>
          )}
        </div>
      </div>
    </div>
  );
}
